import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router';

import { Tab } from '../sticky-tab-nav/types';

import * as S from './styles';

type TabProps = Tab & {
  onClick?: (tab: string) => void;
};

type OwnProps = {
  className?: string;
  onClick?: (tab: string) => void;
  tabs: Tab[];
  background?: boolean;
  borderBottom?: boolean;
  isSticky?: boolean;
};

const TabItem = ({
  active,
  id,
  onClick,
  scrollToTop,
  tabCallback,
  title,
  to,
}: TabProps) => {
  const history = useHistory();
  const handleOnClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (scrollToTop) {
        e.preventDefault();
        e.stopPropagation();

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Remove potential hash from url
        history.push(history.location.pathname + history.location.search);
      }

      if (!onClick) return null;
      return onClick(id);
    },
    [id, onClick, scrollToTop, history]
  );

  // Handles fetching data for active tab if reloading page with URL hash
  useEffect(() => {
    if (active && tabCallback) tabCallback();
  }, [active]);

  return (
    <S.TabLink
      active={active}
      onClick={handleOnClick}
      to={to}
      // A callback that runs on hover, used for fetching the tab content data in advance
      // to make the tab content show earlier without placeholders when eventually clicking the tab.
      onMouseEnter={tabCallback}
    >
      {title}
    </S.TabLink>
  );
};

export const Tabs = React.forwardRef<HTMLElement, OwnProps>(
  ({ className, borderBottom, onClick, tabs, background, isSticky }, ref) => (
    <S.Tabs className={className} ref={ref}>
      <S.TabsBackground
        background={background}
        borderBottom={borderBottom}
        isSticky={isSticky}
      >
        <S.Wrapper>
          {tabs.map(tab => (
            <TabItem {...tab} key={tab.id} onClick={onClick} />
          ))}
        </S.Wrapper>
      </S.TabsBackground>
    </S.Tabs>
  )
);
